/**
 * PainelPage (M10, RF-M10-001) — painel de totais: grid de KpiCard.
 *
 * Acesso universal de leitura (RN-M7-001 / consulta+editor+admin). Quando a
 * seção de totais está `indisponivel` (degradação por seção, AC4), os cards
 * mostram "indisponível" sem derrubar a página. Polling ≤ 60s pelo hook.
 * Abaixo dos KPIs, o quadro "Documentos sem visita" (ADR-034) explica a diferença entre
 * arquivos e visitas, por motivo. Para o administrador, o cartão "Sugestões" (ADR-036) avisa quando vale
 * reprocessar e abre o diálogo de reprocessamento — nada é disparado sozinho.
 * ADR-038 (design §9): o cartão "Visitas" separa pela evidência ("N com imagem · M planilha (sem imagem)") e o filtro
 * Evidência restringe os totais de visita a um tipo (os arquivos não mudam).
 */

import { KpiCard } from '../components/KpiCard';
import { DocumentosSemVisitaQuadro } from '../components/DocumentosSemVisitaQuadro';
import { SugestoesReprocessamento } from '../components/SugestoesReprocessamento';
import { useState } from 'react';
import { useTotais } from '../hooks/usePainel';
import { FiltroEvidencia } from '../components/Evidencia';
import { Evidencia } from '../services/consulta-types';

const numero = new Intl.NumberFormat('pt-BR');

/** "1.200 com imagem · 34 planilha (sem imagem)" — só quando a API traz a separação (ADR-038). */
function detalheVisitas(comImagem: number | undefined, semImagem: number | undefined): string | undefined {
  if (comImagem === undefined || semImagem === undefined) return undefined;
  return `${numero.format(comImagem)} com imagem · ${numero.format(semImagem)} planilha (sem imagem)`;
}

export function PainelPage(): JSX.Element {
  const [evidencia, setEvidencia] = useState<Evidencia | undefined>(undefined);
  const { totais, carregando, erro } = useTotais(evidencia);
  const indisponivel = erro;

  const t = totais ?? {
    arquivos: 0,
    visitas: 0,
    escolas: 0,
    produtivas: 0,
    improdutivas: 0,
    suspeitos: 0,
    editadas: 0,
  };

  return (
    <div>
      <h1 className="page-title">Painel</h1>
      <p className="page-subtitle">
        Visão gerencial do acervo — totais atualizados automaticamente.
      </p>

      <div className="filtros">
        <FiltroEvidencia id="painel-evidencia" semReferencias valor={{ evidencia }} onMudar={(v) => setEvidencia(v.evidencia)} />
      </div>

      {carregando && <p className="muted">Carregando…</p>}

      <div className="kpi-grid">
        <KpiCard rotulo="Arquivos" valor={t.arquivos} indisponivel={indisponivel} />
        <KpiCard
          rotulo="Visitas"
          valor={t.visitas}
          indisponivel={indisponivel}
          detalhe={detalheVisitas(totais?.comImagem, totais?.semImagem)}
        />
        <KpiCard rotulo="Escolas" valor={t.escolas} indisponivel={indisponivel} />
        <KpiCard rotulo="Produtivas" valor={t.produtivas} indisponivel={indisponivel} />
        <KpiCard rotulo="Improdutivas" valor={t.improdutivas} indisponivel={indisponivel} />
        <KpiCard rotulo="Suspeitos" valor={t.suspeitos} indisponivel={indisponivel} />
        <KpiCard rotulo="Editadas" valor={t.editadas} indisponivel={indisponivel} />
      </div>

      {indisponivel && (
        <p className="notice">
          Alguns indicadores estão temporariamente indisponíveis. Tentaremos novamente.
        </p>
      )}

      <SugestoesReprocessamento />

      <DocumentosSemVisitaQuadro />
    </div>
  );
}
